import { useMemo, useState } from "react"
import { AlertTriangle, CheckCircle2, Clock, RotateCcw, XCircle } from "lucide-react"
import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { DatePopover } from "@/components/date-popover"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { StatusBadge } from "@/components/status-badge"
import { Textarea } from "@/components/ui/textarea"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { LockedBanner } from "@/features/transit/components/locked-banner"
import { SectionTitle } from "@/features/transit/components/section-title"
import { UploadButton } from "@/features/transit/components/upload-button"
import { UploadMetadataDialog } from "@/features/transit/components/upload-metadata-dialog"
import { UploadedDocRow } from "@/features/transit/components/uploaded-doc-row"
import {
  useClearCCC,
  useDeleteCCCDoc,
  useIssueCCC,
  useReprocessLot,
} from "@/features/transit/hooks/use-lot-mutations"
import { useTransitUpload } from "@/features/transit/hooks/use-transit-upload"
import { transitKeys } from "@/features/transit/hooks/use-transit-orders"
import { useUploadWithMetadata } from "@/features/transit/hooks/use-upload-with-metadata"
import { registerCCCDoc as registerCCCDocApi } from "@/features/transit/api/transit.api"
import { resolveApiError } from "@/lib/api/error-i18n"
import {
  bvExpiresAt,
  daysUntilExpiry,
  isBVExpired,
} from "@/features/transit/lib/ccc"
import { isOTLocked } from "@/features/transit/lib/ot-math"
import type {
  CCCKind,
  IssueCCCPayload,
  OT,
  OTLotLink,
} from "@/features/transit/api/types"

type DraftKind = "" | CCCKind

interface CCCDraft {
  kind: DraftKind
  concessionnaireName: string
  bvNumber: string
  bvDate: string
  baDocNumber: string
  reason: string
}

const EMPTY_DRAFT: CCCDraft = {
  kind: "",
  concessionnaireName: "",
  bvNumber: "",
  bvDate: "",
  baDocNumber: "",
  reason: "",
}

function todayISO(): string {
  return new Date().toISOString().slice(0, 10)
}

interface AnalysePanelProps {
  ot: OT
}

export function AnalysePanel({ ot }: AnalysePanelProps) {
  const { t } = useTranslation("transit")
  // Sort by link id so an update to one card doesn't reshuffle the
  // grid — PostgREST embed order isn't deterministic across writes.
  const links = useMemo(
    () => [...(ot.lots ?? [])].sort((a, b) => a.id - b.id),
    [ot.lots],
  )
  const locked = isOTLocked(ot)

  return (
    <div className="flex flex-col gap-4">
      {locked ? <LockedBanner ot={ot} /> : null}
      <Card className="flex flex-col gap-4 p-5">
        <SectionTitle
          eyebrow={t("analyse.eyebrow")}
          title={t("analyse.title")}
          description={t("analyse.description")}
        />

        {links.length === 0 ? (
          <p className="text-muted-foreground rounded-md border border-dashed py-6 text-center text-sm">
            {t("analyse.empty")}
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            {links.map((link) => (
              <LotCard key={link.id} ot={ot} link={link} />
            ))}
          </div>
        )}
      </Card>
    </div>
  )
}

function LotCard({ ot, link }: { ot: OT; link: OTLotLink }) {
  const { t } = useTranslation("transit")
  const issue = useIssueCCC(ot.id)
  const clear = useClearCCC(ot.id)
  const reprocess = useReprocessLot(ot.id)
  const deleteDoc = useDeleteCCCDoc(ot.id)
  const upload = useTransitUpload()
  const queryClient = useQueryClient()
  const locked = isOTLocked(ot)

  // Single source of truth for the structured CCC form. The form is
  // only mounted before a decision exists; once issued we render the
  // read-only summary instead.
  const [draft, setDraft] = useState<CCCDraft>(EMPTY_DRAFT)

  const decision = link.cccKind
  const docs = link.cccDocs ?? []
  const expired = isBVExpired(link.cccBVDate)

  // We hit `registerCCCDoc` directly here instead of going through the
  // useRegisterCCCDoc mutation hook. The hook awaits a post-success
  // refetch before resolving mutateAsync, which keeps `upload.isUploading`
  // pinned to true until the (heavy) OT detail refetch completes. From the
  // operator's POV that looks like the button is stuck after the file
  // visibly arrived in the docs list. Talking to the API directly lets
  // the button clear the moment the network POST returns; we still
  // invalidate the OT cache, just without blocking the loader on it.
  const meta = useUploadWithMetadata({
    kindLabel: t("analyse.docsLabel"),
    upload: async (file, m) => {
      try {
        await upload.upload({
          otId: ot.id,
          kind: "cccDoc",
          otLotId: link.id,
          file,
          register: async ({ storedKey, file: f }) =>
            registerCCCDocApi(ot.id, link.lotId, {
              name: m.name,
              storedKey,
              docType: f.type || undefined,
              uploadedBy: m.uploadedBy || undefined,
              note: m.description || undefined,
            }),
        })
      } catch (err) {
        toast.error(resolveApiError(err, t))
        throw err
      } finally {
        // Best-effort refresh — fired after the loader clears so the
        // operator isn't waiting on a network round-trip to drop the
        // spinner.
        void queryClient.refetchQueries({
          queryKey: transitKeys.detail(ot.id),
          type: "active",
        })
        void queryClient.invalidateQueries({ queryKey: transitKeys.all })
      }
    },
  })

  return (
    <div className="border-border/70 rounded-md border bg-card overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3">
        <div className="min-w-0 flex-1">
          <div className="font-mono text-sm font-medium truncate">
            {link.lot?.lotNumber ?? `#${link.lotId}`}
          </div>
          <div className="text-muted-foreground mt-0.5 text-[11px] tabular-nums">
            {link.lot?.bags ?? "—"} sacs · {((link.lot?.netKg ?? 0) / 1000).toFixed(2)}{" "}
            MT
          </div>
        </div>
        <DecisionBadge kind={decision} expired={expired} />
      </div>

      <div className="border-border/70 border-t bg-muted/30 px-4 py-4">
        {decision === null ? (
          <CCCDecisionForm
            draft={draft}
            onChange={setDraft}
            onSubmit={(payload) =>
              issue.mutate(
                { lotId: link.lotId, payload },
                { onSuccess: () => setDraft(EMPTY_DRAFT) },
              )
            }
            isSubmitting={issue.isPending}
            locked={locked}
          />
        ) : (
          <div className="flex flex-col gap-3">
            <div className="text-xs">
              {decision === "BV" ? (
                <BVSummary link={link} expired={expired} />
              ) : (
                <BASummary
                  link={link}
                  onReprocess={() => reprocess.mutate(link.lotId)}
                  reprocessing={reprocess.isPending}
                  locked={locked}
                />
              )}
            </div>

            <div>
              <div className="mb-1.5 flex items-center justify-between">
                <span className="text-muted-foreground text-[11px] font-semibold uppercase tracking-wide">
                  {t("analyse.docsLabel")} ({docs.length})
                  {decision === "BV" ? (
                    <span className="text-red-500"> *</span>
                  ) : null}
                </span>
                {locked ? null : (
                  <UploadButton
                    onPick={meta.onPick}
                    isUploading={upload.isUploading}
                    size="sm"
                    variant="outline"
                  />
                )}
              </div>
              {docs.length === 0 ? (
                decision === "BV" ? (
                  <div className="border-amber-200 bg-amber-50/60 dark:border-amber-500/30 dark:bg-amber-500/10 flex items-start gap-2 rounded-md border p-2 text-xs">
                    <AlertTriangle className="text-amber-600 mt-0.5 size-3.5 flex-shrink-0" />
                    <p className="text-foreground/80">
                      {t("analyse.bvDocRequired")}
                    </p>
                  </div>
                ) : (
                  <p className="text-muted-foreground/70 text-[11px] italic">
                    {t("analyse.noDocs")}
                  </p>
                )
              ) : (
                <ul className="space-y-2">
                  {docs.map((d) => (
                    <li key={d.id}>
                      <UploadedDocRow
                        otId={ot.id}
                        doc={d}
                        onDelete={() =>
                          deleteDoc.mutate({
                            lotId: link.lotId,
                            docId: d.id,
                          })
                        }
                        isDeleting={deleteDoc.isPending}
                        locked={locked}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {locked ? null : (
              <div className="border-border/60 flex justify-end border-t pt-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => clear.mutate(link.lotId)}
                  disabled={clear.isPending}
                >
                  <RotateCcw className="size-3.5" />
                  {t("analyse.clearBtn")}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
      <UploadMetadataDialog
        pending={meta.pending}
        onCancel={meta.onCancel}
        onConfirm={meta.onConfirm}
      />
    </div>
  )
}

function DecisionBadge({
  kind,
  expired,
}: {
  kind: OTLotLink["cccKind"]
  expired: boolean
}) {
  const { t } = useTranslation("transit")
  if (!kind) {
    return <StatusBadge tone="warning">{t("analyse.awaitingShort")}</StatusBadge>
  }
  if (kind === "BV") {
    if (expired) {
      return <StatusBadge tone="danger">{t("analyse.bvExpiredShort")}</StatusBadge>
    }
    return <StatusBadge tone="success">BV</StatusBadge>
  }
  return <StatusBadge tone="danger">BA</StatusBadge>
}

// ── CCC decision form ────────────────────────────────────────────────
// Kind picker first, then conditional fields. We let the kind be empty
// in local state so the form opens with no decision pre-selected — the
// operator picks one explicitly.

function CCCDecisionForm({
  draft,
  onChange,
  onSubmit,
  isSubmitting,
  locked,
}: {
  draft: CCCDraft
  onChange: (next: CCCDraft) => void
  onSubmit: (payload: IssueCCCPayload) => void
  isSubmitting: boolean
  locked: boolean
}) {
  const { t } = useTranslation("transit")

  const canSubmitBV =
    draft.kind === "BV" && draft.concessionnaireName.trim() !== "" && draft.bvDate !== ""
  const canSubmitBA =
    draft.kind === "BA" && draft.baDocNumber.trim() !== ""
  const canSubmit = (canSubmitBV || canSubmitBA) && !isSubmitting && !locked

  const submit = () => {
    if (draft.kind === "BV") {
      onSubmit({
        kind: "BV",
        concessionnaireName: draft.concessionnaireName.trim() || undefined,
        bvNumber: draft.bvNumber.trim() || undefined,
        bvDate: draft.bvDate || undefined,
      })
      return
    }
    if (draft.kind === "BA") {
      onSubmit({
        kind: "BA",
        baDocNumber: draft.baDocNumber.trim() || undefined,
        reason: draft.reason.trim() || undefined,
      })
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-muted-foreground text-xs">{t("analyse.awaitingHelp")}</p>

      <div className="flex flex-col gap-1.5">
        <Label className="text-xs">{t("analyse.kindLabel")}</Label>
        <Select
          value={draft.kind}
          onValueChange={(v) =>
            onChange({ ...draft, kind: v as DraftKind })
          }
          disabled={locked}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder={t("analyse.kindPlaceholder")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="BV">{t("analyse.kindBV")}</SelectItem>
            <SelectItem value="BA">{t("analyse.kindBA")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {draft.kind === "BV" ? (
        <BVFormFields draft={draft} onChange={onChange} locked={locked} />
      ) : null}
      {draft.kind === "BA" ? (
        <BAFormFields draft={draft} onChange={onChange} locked={locked} />
      ) : null}

      {draft.kind === "" ? null : (
        <div className="flex justify-end">
          <Button
            onClick={submit}
            disabled={!canSubmit}
            variant={draft.kind === "BV" ? "default" : "outline"}
            className={
              draft.kind === "BA" ? "text-red-600 hover:text-red-700" : undefined
            }
          >
            {draft.kind === "BV" ? (
              <>
                <CheckCircle2 className="size-4" />
                {t("analyse.issueBV")}
              </>
            ) : (
              <>
                <XCircle className="size-4" />
                {t("analyse.issueBA")}
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  )
}

function BVFormFields({
  draft,
  onChange,
  locked,
}: {
  draft: CCCDraft
  onChange: (next: CCCDraft) => void
  locked: boolean
}) {
  const { t } = useTranslation("transit")
  // Default the BV date to today the first time the user opens the BV
  // form. We only seed on demand so the operator can blank it back out
  // if they're entering a historic decision.
  const bvDate = draft.bvDate || ""
  const expiresAt = bvExpiresAt(bvDate)

  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      <div className="flex flex-col gap-1.5">
        <Label className="text-xs">
          {t("analyse.concessionnaireLabel")}
          <span className="text-red-500"> *</span>
        </Label>
        <Input
          value={draft.concessionnaireName}
          onChange={(e) =>
            onChange({ ...draft, concessionnaireName: e.target.value })
          }
          placeholder={t("analyse.concessionnairePlaceholder")}
          disabled={locked}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label className="flex items-center gap-1 text-xs">
          {t("analyse.bvNumberLabel")}
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-muted-foreground cursor-help">ⓘ</span>
            </TooltipTrigger>
            <TooltipContent>{t("analyse.bvNumberHelp")}</TooltipContent>
          </Tooltip>
        </Label>
        <Input
          value={draft.bvNumber}
          onChange={(e) => onChange({ ...draft, bvNumber: e.target.value })}
          placeholder={t("analyse.bvNumberPlaceholder")}
          disabled={locked}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label className="text-xs">
          {t("analyse.bvDateLabel")}
          <span className="text-red-500"> *</span>
        </Label>
        <DatePopover
          value={bvDate}
          onChange={(iso) =>
            onChange({ ...draft, bvDate: iso || todayISO() })
          }
          disabled={locked}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label className="text-xs">{t("analyse.bvExpiryLabel")}</Label>
        <div className="bg-muted/40 text-muted-foreground rounded-md border px-3 py-2 text-xs">
          {expiresAt
            ? t("analyse.bvExpiryPreview", {
                at: expiresAt.toISOString().slice(0, 10),
              })
            : t("analyse.bvExpiryEmpty")}
        </div>
      </div>
    </div>
  )
}

function BAFormFields({
  draft,
  onChange,
  locked,
}: {
  draft: CCCDraft
  onChange: (next: CCCDraft) => void
  locked: boolean
}) {
  const { t } = useTranslation("transit")
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-1.5">
        <Label className="text-xs">
          {t("analyse.baDocNumberLabel")}
          <span className="text-red-500"> *</span>
        </Label>
        <Input
          value={draft.baDocNumber}
          onChange={(e) => onChange({ ...draft, baDocNumber: e.target.value })}
          placeholder={t("analyse.baDocNumberPlaceholder")}
          disabled={locked}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label className="text-xs">{t("analyse.baReasonLabel")}</Label>
        <Textarea
          value={draft.reason}
          onChange={(e) => onChange({ ...draft, reason: e.target.value })}
          placeholder={t("analyse.baReasonPlaceholder")}
          rows={2}
          disabled={locked}
        />
      </div>
    </div>
  )
}

// ── Issued-state summaries ────────────────────────────────────────────

function BVSummary({ link, expired }: { link: OTLotLink; expired: boolean }) {
  const { t } = useTranslation("transit")
  const remaining = daysUntilExpiry(link.cccBVDate)
  return (
    <div className="flex flex-col gap-1">
      <div className="text-muted-foreground">
        {t("analyse.bvIssuedAt", {
          at: link.cccBVDate ?? link.cccIssuedAt?.slice(0, 10) ?? "—",
        })}
        {link.cccConcessionnaireName ? (
          <>
            {" · "}
            <span className="text-foreground font-medium">
              {link.cccConcessionnaireName}
            </span>
          </>
        ) : null}
        {link.cccBvNumber ? (
          <>
            {" · "}
            <span className="font-mono">{link.cccBvNumber}</span>
          </>
        ) : null}
      </div>
      <ExpiryChip remainingDays={remaining} expired={expired} />
    </div>
  )
}

function ExpiryChip({
  remainingDays,
  expired,
}: {
  remainingDays: number | null
  expired: boolean
}) {
  const { t } = useTranslation("transit")
  if (remainingDays == null) return null
  if (expired) {
    return (
      <span className="inline-flex items-center gap-1 self-start rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-medium text-red-700 dark:bg-red-500/15 dark:text-red-300">
        <AlertTriangle className="size-3" />
        {t("analyse.bvExpiredOn", { days: Math.abs(remainingDays) })}
      </span>
    )
  }
  return (
    <span className="text-muted-foreground inline-flex items-center gap-1 self-start text-[10px]">
      <Clock className="size-3" />
      {t("analyse.bvExpiresIn", { days: remainingDays })}
    </span>
  )
}

function BASummary({
  link,
  onReprocess,
  reprocessing,
  locked,
}: {
  link: OTLotLink
  onReprocess: () => void
  reprocessing: boolean
  locked: boolean
}) {
  const { t } = useTranslation("transit")
  return (
    <div className="space-y-1.5">
      <div className="text-muted-foreground">
        {t("analyse.baIssuedAt", {
          at: link.cccIssuedAt?.slice(0, 10) ?? "—",
        })}
        {link.cccBADocNumber ? (
          <>
            {" · "}
            <span className="font-mono">{link.cccBADocNumber}</span>
          </>
        ) : null}
      </div>
      {link.cccReason ? (
        <p className="text-foreground text-xs">{link.cccReason}</p>
      ) : null}
      {link.needsReprocessing && !locked ? (
        <Button
          variant="outline"
          size="sm"
          onClick={onReprocess}
          disabled={reprocessing}
        >
          <RotateCcw className="size-3.5" />
          {t("analyse.reprocessBtn")}
        </Button>
      ) : null}
    </div>
  )
}
